import { ReactNode, useMemo } from 'react';
import { ConditionalWrapperCriteria } from './types';
import { resolveVisibility } from './resolveVisibility';

export interface ConditionalWrapperProps<ContextType extends object> {
  context: ContextType;
  whitelist?: ConditionalWrapperCriteria<ContextType>[];
  blacklist?: ConditionalWrapperCriteria<ContextType>[];
  children?: ReactNode;
  render?: (
    isVisible: boolean,
    matchedCriteria?: ConditionalWrapperCriteria<ContextType>,
  ) => ReactNode;
}

/**
 * Conditionally renders children based on whitelist and blacklist criteria.
 *
 * Evaluation order:
 * 1. If context matches any blacklist criteria: hidden
 * 2. If no whitelist is defined: visible
 * 3. If context matches any whitelist criteria: visible
 * 4. Otherwise: hidden
 *
 * @template ContextType - The type of the context object
 * @param props.context - The context object to evaluate against criteria
 * @param props.blacklist - Optional array of criteria. If any match, children
 *  are hidden regardless of whitelist
 * @param props.whitelist - Optional array of criteria. If provided, children
 *  are only visible if any criteria match
 * @param props.children - Content to render when visible (cannot be used with
 *  render prop)
 * @param props.render - Optional render function that receives visibility state
 *  and matched criteria (cannot be used with children)
 * @throws Error if both children and render are provided
 */
export const ConditionalWrapper = <ContextType extends object>({
  children,
  render,
  context,
  blacklist,
  whitelist,
}: ConditionalWrapperProps<ContextType>) => {
  const [isVisible, matchedCriteria] = useMemo(
    () => resolveVisibility({ context, blacklist, whitelist }),
    [context, blacklist, whitelist],
  );

  if (children && render) throw Error('Cannot define both children && render');

  if (render) {
    return render(isVisible, matchedCriteria);
  }

  return isVisible ? children : null;
};
